import { useEffect } from 'react';
import { X, AlertTriangle, HelpCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { clsx } from 'clsx';

export interface ConfirmModalProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}

export function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'danger',
  onConfirm,
  onCancel,
  loading = false,
}: ConfirmModalProps) {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onCancel();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onCancel, loading]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div className="bg-surface border border-border rounded-2xl w-full max-w-md flex flex-col shadow-2xl overflow-hidden animate-scale-in">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-surface-secondary/40">
          <div className="flex items-center gap-2.5 min-w-0">
            <div
              className={clsx(
                'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                variant === 'danger'
                  ? 'bg-brand-danger/10 text-brand-danger'
                  : 'bg-brand-primary/10 text-brand-primary'
              )}
            >
              {variant === 'danger' ? <AlertTriangle size={16} /> : <HelpCircle size={16} />}
            </div>
            <h3 id="confirm-modal-title" className="font-bold text-text-primary text-sm truncate">
              {title}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            aria-label="Cerrar modal"
            className="p-1.5 rounded-lg text-text-muted hover:text-text-primary hover:bg-surface-tertiary transition-colors cursor-pointer disabled:opacity-40 shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 text-xs text-text-secondary leading-relaxed">
          <p>{message}</p>
        </div>

        {/* Modal Footer */}
        <div className="p-3 border-t border-border bg-surface-secondary/30 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loading}
            onClick={onCancel}
            className="text-xs"
          >
            {cancelLabel}
          </Button>
          <Button
            type="button"
            variant={variant === 'danger' ? 'danger' : 'primary'}
            size="sm"
            disabled={loading}
            onClick={onConfirm}
            className="text-xs"
          >
            {loading ? (
              <>
                <Loader2 size={13} className="animate-spin mr-1" />
                <span>Procesando...</span>
              </>
            ) : (
              <span>{confirmLabel}</span>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
